import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import CardByCarousel, {
    getKitchenCarouselCardHeightPx,
    getKitchenCarouselCardWrapperSx,
    getKitchenCarouselNavTopPercentFromCardWidth,
    KITCHEN_CAROUSEL_IMAGE_HEIGHT_CQW_RATIO,
    KITCHEN_CARD_HEIGHT_DEFAULT,
    KITCHEN_CARD_LAYOUT,
    KITCHEN_CAROUSEL_CARD_SELECTOR,
    KITCHEN_CAROUSEL_CARD_TEXT_BLOCK_HEIGHT,
    KITCHEN_CAROUSEL_CARD_TEXT_BLOCK_HEIGHT_COMPACT,
    KITCHEN_CAROUSEL_MAX_WIDTH_1023_PX,
} from "./CardByCarousel";
import {
    CAROUSEL_TRANSITION_MS,
    CarouselNavIcon,
    kitchenNavButtonSx,
} from "./carouselNavShared";

const KITCHEN_CAROUSEL_NAV_INSET = 20;
const KITCHEN_CAROUSEL_NAV_TOP_VERTICAL_PX = 25;
const KITCHEN_CAROUSEL_SLIDE_GAP = 20;
const CARD_COMPACT_MQ = "(max-width: 1440px)";
const CARD_STACK_MQ = "(max-width: 1023px)";
const CARD_STACK_MEDIA_SX = "@media (max-width: 1023px)";
const CARDS_PER_SLIDE = 2;

const kitchenCarouselRootSx = {
    position: "relative",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    [CARD_STACK_MEDIA_SX]: {
        maxWidth: `${KITCHEN_CAROUSEL_MAX_WIDTH_1023_PX}px`,
        marginLeft: "auto",
        marginRight: "auto",
    },
};

const kitchenSlideSx = {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: `${KITCHEN_CAROUSEL_SLIDE_GAP}px`,
    width: "100%",
    height: "100%",
    flexShrink: 0,
    boxSizing: "border-box",
    [CARD_STACK_MEDIA_SX]: {
        flexDirection: "column",
    },
};

const kitchenNavRowSx = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 2,
};

const kitchenNavButtonWrapSx = (side, isVertical = false) => ({
    position: "absolute",
    ...(isVertical
        ? {
            left: "50%",
            right: "auto",
            transform: "translateX(-50%)",
            ...(side === "next"
                ? {bottom: `${KITCHEN_CAROUSEL_NAV_INSET}px`, top: "auto"}
                : {top: `${KITCHEN_CAROUSEL_NAV_TOP_VERTICAL_PX}px`, bottom: "auto"}),
        }
        : {
            top: "var(--kitchen-nav-top, 50%)",
            transform: "translateY(-50%)",
            ...(side === "next"
                ? {right: `${KITCHEN_CAROUSEL_NAV_INSET}px`, left: "auto"}
                : {left: `${KITCHEN_CAROUSEL_NAV_INSET}px`, right: "auto"}),
        }),
    pointerEvents: "auto",
});

export const buildKitchenCarouselSlides = (items = []) => {
    if (items.length === 3) {
        return [
            [items[0], items[1]],
            [items[2], items[0]],
            [items[1], items[2]],
        ];
    }

    const slides = [];
    for (let i = 0; i < items.length; i += CARDS_PER_SLIDE) {
        const pair = items.slice(i, i + CARDS_PER_SLIDE);
        if (pair.length > 0) {
            slides.push(pair);
        }
    }
    return slides;
};

export const KuhniKitchenCarousel = ({
    banners = [],
    adjacentNextBanners = [],
    adjacentPrevBanners = [],
    onPageNext,
    onPagePrev,
}) => {
    const navigate = useNavigate();
    const kitchenCarouselRef = useRef(null);
    const trackRef = useRef(null);
    const isPageTransitionRef = useRef(false);
    const pageTransitionDirectionRef = useRef(null);

    const isKitchenCompact = useMediaQuery(CARD_COMPACT_MQ);
    const isStackAt1023 = useMediaQuery(CARD_STACK_MQ);
    const isKitchenHeight1250 = useMediaQuery("(max-width:1250px)");
    const isKitchenHeight1450 = useMediaQuery("(max-width:1450px)");
    const [kitchenNavTopPercent, setKitchenNavTopPercent] = useState(50);
    const [kitchenCarouselHeight, setKitchenCarouselHeight] = useState(KITCHEN_CARD_HEIGHT_DEFAULT);
    const [slideIndex, setSlideIndex] = useState(0);
    const [trackSlides, setTrackSlides] = useState(() => buildKitchenCarouselSlides(banners));
    const [enableTransition, setEnableTransition] = useState(true);

    const kitchenCardLayout = isKitchenHeight1250
        ? KITCHEN_CARD_LAYOUT.at1250
        : isKitchenHeight1450
            ? KITCHEN_CARD_LAYOUT.at1450
            : KITCHEN_CARD_LAYOUT.default;

    const kitchenCardWrapperSx = getKitchenCarouselCardWrapperSx(false);
    const currentSlides = buildKitchenCarouselSlides(banners);
    const nextPageSlides = buildKitchenCarouselSlides(adjacentNextBanners);
    const prevPageSlides = buildKitchenCarouselSlides(adjacentPrevBanners);

    const updateKitchenCarouselLayout = useCallback(() => {
        const carouselEl = kitchenCarouselRef.current;
        if (!carouselEl || banners.length === 0) {
            setKitchenCarouselHeight(0);
            return;
        }

        const carouselWidth = carouselEl.getBoundingClientRect().width;
        if (carouselWidth <= 0) {
            return;
        }

        const cardWidth = isStackAt1023
            ? Math.min(carouselWidth, KITCHEN_CAROUSEL_MAX_WIDTH_1023_PX)
            : (carouselWidth - KITCHEN_CAROUSEL_SLIDE_GAP * Math.max(0, CARDS_PER_SLIDE - 1)) / CARDS_PER_SLIDE;
        const contentHeight = isKitchenCompact
            ? KITCHEN_CAROUSEL_CARD_TEXT_BLOCK_HEIGHT_COMPACT
            : KITCHEN_CAROUSEL_CARD_TEXT_BLOCK_HEIGHT;
        const layout = {
            ...kitchenCardLayout,
            padding: isKitchenCompact ? 16 : kitchenCardLayout.padding,
            gap: isKitchenCompact ? 16 : kitchenCardLayout.gap,
            contentHeight,
            imageHeightRatio: KITCHEN_CAROUSEL_IMAGE_HEIGHT_CQW_RATIO,
        };

        const formulaHeight = getKitchenCarouselCardHeightPx(cardWidth, layout);
        const measuredCard = carouselEl.querySelector(KITCHEN_CAROUSEL_CARD_SELECTOR);
        const measuredHeight = measuredCard
            ? Math.ceil(measuredCard.getBoundingClientRect().height)
            : 0;
        const baseCardHeight = measuredHeight > 0 ? measuredHeight : formulaHeight;
        const cardsInSlide = trackSlides[slideIndex]?.length ?? CARDS_PER_SLIDE;
        const nextHeight = isStackAt1023
            ? baseCardHeight * cardsInSlide + KITCHEN_CAROUSEL_SLIDE_GAP * Math.max(0, cardsInSlide - 1)
            : baseCardHeight;
        setKitchenNavTopPercent(
            isStackAt1023
                ? 50
                : getKitchenCarouselNavTopPercentFromCardWidth(cardWidth, layout),
        );
        setKitchenCarouselHeight((prev) => (Math.abs(prev - nextHeight) <= 1 ? prev : nextHeight));
    }, [banners.length, isKitchenCompact, isStackAt1023, kitchenCardLayout, slideIndex, trackSlides]);

    useEffect(() => {
        if (isPageTransitionRef.current) {
            return;
        }
        const slides = buildKitchenCarouselSlides(banners);
        setTrackSlides(slides);
        setSlideIndex(0);
        setEnableTransition(false);
        const rafId = requestAnimationFrame(() => {
            requestAnimationFrame(() => setEnableTransition(true));
        });
        return () => cancelAnimationFrame(rafId);
    }, [banners]);

    useEffect(() => {
        const carouselEl = kitchenCarouselRef.current;
        if (!carouselEl) {
            return undefined;
        }

        updateKitchenCarouselLayout();
        const rafIds = [
            requestAnimationFrame(updateKitchenCarouselLayout),
            requestAnimationFrame(() => requestAnimationFrame(updateKitchenCarouselLayout)),
        ];
        window.addEventListener("resize", updateKitchenCarouselLayout);

        return () => {
            rafIds.forEach((id) => cancelAnimationFrame(id));
            window.removeEventListener("resize", updateKitchenCarouselLayout);
        };
    }, [updateKitchenCarouselLayout, banners.length, trackSlides.length, slideIndex]);

    const resetTrackAfterWrap = useCallback((targetSlideIndex) => {
        const slides = buildKitchenCarouselSlides(banners);
        setEnableTransition(false);
        setTrackSlides(slides);
        setSlideIndex(targetSlideIndex);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => setEnableTransition(true));
        });
    }, [banners]);

    const finishPageTransition = useCallback(() => {
        const direction = pageTransitionDirectionRef.current;
        isPageTransitionRef.current = false;
        pageTransitionDirectionRef.current = null;
        if (direction === "next") {
            onPageNext?.();
        } else if (direction === "prev") {
            onPagePrev?.();
        }
    }, [onPageNext, onPagePrev]);

    const handleTrackTransitionEnd = useCallback((event) => {
        if (event.target !== trackRef.current || event.propertyName !== "transform") {
            return;
        }
        if (!isPageTransitionRef.current) {
            return;
        }

        const direction = pageTransitionDirectionRef.current;
        if (direction === "wrap-next") {
            isPageTransitionRef.current = false;
            pageTransitionDirectionRef.current = null;
            resetTrackAfterWrap(0);
            return;
        }
        if (direction === "wrap-prev") {
            isPageTransitionRef.current = false;
            pageTransitionDirectionRef.current = null;
            resetTrackAfterWrap(currentSlides.length - 1);
            return;
        }

        finishPageTransition();
    }, [currentSlides.length, finishPageTransition, resetTrackAfterWrap]);

    const goToNextSlide = useCallback(() => {
        if (isPageTransitionRef.current) {
            return;
        }

        const lastCurrentIndex = currentSlides.length - 1;
        if (slideIndex < lastCurrentIndex) {
            setSlideIndex((prev) => prev + 1);
            return;
        }

        if (lastCurrentIndex <= 0) {
            return;
        }

        isPageTransitionRef.current = true;
        pageTransitionDirectionRef.current = "wrap-next";
        setTrackSlides([...currentSlides, currentSlides[0]]);
        setEnableTransition(false);
        setSlideIndex(lastCurrentIndex);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setEnableTransition(true);
                setSlideIndex(lastCurrentIndex + 1);
            });
        });
    }, [currentSlides, slideIndex]);

    const goToPrevSlide = useCallback(() => {
        if (isPageTransitionRef.current) {
            return;
        }

        if (slideIndex > 0) {
            setSlideIndex((prev) => prev - 1);
            return;
        }

        const lastCurrentIndex = currentSlides.length - 1;
        if (lastCurrentIndex <= 0) {
            return;
        }

        isPageTransitionRef.current = true;
        pageTransitionDirectionRef.current = "wrap-prev";
        setTrackSlides([currentSlides[lastCurrentIndex], ...currentSlides]);
        setEnableTransition(false);
        setSlideIndex(1);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setEnableTransition(true);
                setSlideIndex(0);
            });
        });
    }, [currentSlides, slideIndex]);

    if (!banners.length || trackSlides.length === 0) {
        return null;
    }

    const renderKitchenCard = (item) => (
        <Box key={item.id} className="kitchen-carousel-card" sx={kitchenCardWrapperSx}>
            <CardByCarousel
                catalogCard
                kitchenCarouselCard
                item={item}
                width="100%"
                widthImg="100%"
                compactBelow1440
                onClick={() => navigate(`/catalog/${item.id}`)}
            />
        </Box>
    );

    const kitchenCarouselNavButtonSx = {
        ...kitchenNavButtonSx,
        margin: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
    };

    const kitchenCarouselNavIconSlotSx = {
        width: "21px",
        height: "21px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    };

    const carouselNavIconSx = {
        display: "block",
        width: "14px",
        height: "21px",
        flexShrink: 0,
        transformOrigin: "center center",
        "& path": {
            stroke: "#000000",
        },
    };

    const transitionMs = enableTransition ? CAROUSEL_TRANSITION_MS : 0;

    return (
        <Box
            ref={kitchenCarouselRef}
            sx={{
                ...kitchenCarouselRootSx,
                minHeight: `${kitchenCarouselHeight}px`,
                "--kitchen-nav-top": `${kitchenNavTopPercent}%`,
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    height: `${kitchenCarouselHeight}px`,
                    overflow: "hidden",
                }}
            >
                <Box
                    ref={trackRef}
                    onTransitionEnd={handleTrackTransitionEnd}
                    sx={{
                        display: "flex",
                        flexDirection: isStackAt1023 ? "column" : "row",
                        width: isStackAt1023 ? "100%" : `${trackSlides.length * 100}%`,
                        height: isStackAt1023 ? `${trackSlides.length * 100}%` : "100%",
                        transform: isStackAt1023
                            ? `translateY(-${(slideIndex * 100) / trackSlides.length}%)`
                            : `translateX(-${(slideIndex * 100) / trackSlides.length}%)`,
                        transition: enableTransition
                            ? `transform ${transitionMs}ms cubic-bezier(0.4, 0, 0.2, 1)`
                            : "none",
                        willChange: "transform",
                    }}
                >
                    {trackSlides.map((slideItems, slideKey) => (
                        <Box
                            key={`slide-${slideKey}-${slideItems.map((item) => item.id).join("-")}`}
                            sx={{
                                ...kitchenSlideSx,
                                width: isStackAt1023 ? "100%" : `${100 / trackSlides.length}%`,
                                height: isStackAt1023 ? `${100 / trackSlides.length}%` : "100%",
                            }}
                        >
                            {slideItems.map((item) => renderKitchenCard(item))}
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box sx={kitchenNavRowSx}>
                <Box sx={kitchenNavButtonWrapSx("prev", isStackAt1023)}>
                    <Box
                        component="button"
                        type="button"
                        aria-label="Предыдущий слайд"
                        onClick={goToPrevSlide}
                        sx={kitchenCarouselNavButtonSx}
                    >
                        <Box sx={kitchenCarouselNavIconSlotSx}>
                            <CarouselNavIcon
                                rotate={isStackAt1023 ? "rotate(90deg)" : undefined}
                                iconSx={carouselNavIconSx}
                            />
                        </Box>
                    </Box>
                </Box>
                <Box sx={kitchenNavButtonWrapSx("next", isStackAt1023)}>
                    <Box
                        component="button"
                        type="button"
                        aria-label="Следующий слайд"
                        onClick={goToNextSlide}
                        sx={kitchenCarouselNavButtonSx}
                    >
                        <Box sx={kitchenCarouselNavIconSlotSx}>
                            <CarouselNavIcon
                                rotate={isStackAt1023 ? "rotate(270deg)" : "rotate(180deg)"}
                                iconSx={carouselNavIconSx}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default KuhniKitchenCarousel;
